import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface FamilyMember {
  id: string
  name: string
  dateOfBirth: string
  relationship: 'client' | 'spouse' | 'child' | 'dependent'
  retirementAge?: number
}

export interface Account {
  id: string
  name: string
  type: 'Traditional IRA' | 'Roth IRA' | '401k' | 'Roth 401k' | 'Taxable Brokerage' | 'HSA' | '529' | 'Cash' | 'Other'
  balance: number
  institution: string
  annualContribution: number
  employerMatch?: number
  stocksPercent: number
  bondsPercent: number
  cashPercent: number
}

export interface RealEstate {
  id: string
  type: 'Primary Residence' | 'Rental Property' | 'Vacation Home' | 'Other'
  value: number
  mortgageBalance: number
  monthlyPayment: number
  rentalIncome?: number
}

export interface Income {
  id: string
  source: 'Salary' | 'Bonus' | 'Social Security' | 'Pension' | 'Rental' | 'Other'
  amount: number
  startAge: number
  endAge?: number
  growthRate: number
  frequency: 'Annual' | 'Monthly'
  cola?: number
}

export interface Expense {
  id: string
  category: 'Housing' | 'Healthcare' | 'Living' | 'Discretionary' | 'Education' | 'Charitable' | 'Other'
  amount: number
  inflationRate: number
  startAge?: number
  endAge?: number
}

export interface RiskProfile {
  score: number
  tolerance: 'Conservative' | 'Moderate Conservative' | 'Moderate' | 'Moderate Aggressive' | 'Aggressive'
  stocksPercent: number
  bondsPercent: number
}

export interface FinancialPlan {
  id: string
  clientName: string
  email: string
  advisorCode?: string
  familyMembers: FamilyMember[]
  accounts: Account[]
  realEstate: RealEstate[]
  income: Income[]
  expenses: Expense[]
  riskProfile: RiskProfile | null
  riskAnswers?: { [key: number]: number }
  currentStep: number
  createdAt: string
  updatedAt: string
}

interface FinancialPlanStore {
  currentPlan: FinancialPlan | null
  plans: FinancialPlan[]
  
  // Actions
  createPlan: (clientName: string, email: string, advisorCode?: string) => void
  setCurrentPlanDirect: (plan: FinancialPlan) => void
  updatePlan: (updates: Partial<FinancialPlan>) => void
  addFamilyMember: (member: Omit<FamilyMember, 'id'>) => void
  updateFamilyMember: (id: string, updates: Partial<FamilyMember>) => void
  removeFamilyMember: (id: string) => void
  addAccount: (account: Omit<Account, 'id'>) => void
  updateAccount: (id: string, updates: Partial<Account>) => void
  removeAccount: (id: string) => void
  addRealEstate: (property: Omit<RealEstate, 'id'>) => void
  updateRealEstate: (id: string, updates: Partial<RealEstate>) => void
  removeRealEstate: (id: string) => void
  addIncome: (income: Omit<Income, 'id'>) => void
  updateIncome: (id: string, updates: Partial<Income>) => void
  removeIncome: (id: string) => void
  addExpense: (expense: Omit<Expense, 'id'>) => void
  updateExpense: (id: string, updates: Partial<Expense>) => void
  removeExpense: (id: string) => void
  setRiskProfile: (profile: RiskProfile) => void
  setRiskAnswers: (answers: { [key: number]: number }) => void
  setCurrentStep: (step: number) => void
  loadPlan: (planId: string) => void
  savePlan: () => void
}

export const useFinancialPlanStore = create<FinancialPlanStore>()(
  persist(
    (set, get) => ({
      currentPlan: null,
      plans: [],
      
      createPlan: (clientName: string, email: string, advisorCode?: string) => {
        const newPlan: FinancialPlan = {
          id: crypto.randomUUID(),
          clientName,
          email,
          advisorCode,
          familyMembers: [],
          accounts: [],
          realEstate: [],
          income: [],
          expenses: [],
          riskProfile: null,
          riskAnswers: {},
          currentStep: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        set({ currentPlan: newPlan })
      },

      setCurrentPlanDirect: (plan) => {
        set({ currentPlan: plan })
      },
      
      updatePlan: (updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            ...updates,
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      addFamilyMember: (member) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            familyMembers: [
              ...currentPlan.familyMembers,
              { ...member, id: crypto.randomUUID() }
            ],
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      updateFamilyMember: (id, updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            familyMembers: currentPlan.familyMembers.map(m =>
              m.id === id ? { ...m, ...updates } : m
            ),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      removeFamilyMember: (id) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            familyMembers: currentPlan.familyMembers.filter(m => m.id !== id),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      addAccount: (account) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            accounts: [
              ...currentPlan.accounts,
              { ...account, id: crypto.randomUUID() }
            ],
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      updateAccount: (id, updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            accounts: currentPlan.accounts.map(a =>
              a.id === id ? { ...a, ...updates } : a
            ),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      removeAccount: (id) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            accounts: currentPlan.accounts.filter(a => a.id !== id),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      addRealEstate: (property) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            realEstate: [
              ...currentPlan.realEstate,
              { ...property, id: crypto.randomUUID() }
            ],
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      updateRealEstate: (id, updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            realEstate: currentPlan.realEstate.map(r =>
              r.id === id ? { ...r, ...updates } : r
            ),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      removeRealEstate: (id) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            realEstate: currentPlan.realEstate.filter(r => r.id !== id),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      addIncome: (income) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            income: [
              ...currentPlan.income,
              { ...income, id: crypto.randomUUID() }
            ],
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      updateIncome: (id, updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            income: currentPlan.income.map(i =>
              i.id === id ? { ...i, ...updates } : i
            ),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      removeIncome: (id) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            income: currentPlan.income.filter(i => i.id !== id),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      addExpense: (expense) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            expenses: [
              ...currentPlan.expenses,
              { ...expense, id: crypto.randomUUID() }
            ],
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      updateExpense: (id, updates) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            expenses: currentPlan.expenses.map(e =>
              e.id === id ? { ...e, ...updates } : e
            ),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      removeExpense: (id) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            expenses: currentPlan.expenses.filter(e => e.id !== id),
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      setRiskProfile: (profile) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            riskProfile: profile,
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      setRiskAnswers: (answers) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        set({
          currentPlan: {
            ...currentPlan,
            riskAnswers: { ...(currentPlan.riskAnswers ?? {}), ...answers },
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      setCurrentStep: (step) => {
        const currentPlan = get().currentPlan
        if (!currentPlan) return
        
        set({
          currentPlan: {
            ...currentPlan,
            currentStep: step,
            updatedAt: new Date().toISOString(),
          }
        })
      },
      
      loadPlan: (planId) => {
        const plans = get().plans
        const plan = plans.find(p => p.id === planId)
        if (plan) {
          set({ currentPlan: plan })
        }
      },
      
      savePlan: () => {
        const currentPlan = get().currentPlan
        const plans = get().plans
        if (!currentPlan) return
        
        const existingIndex = plans.findIndex(p => p.id === currentPlan.id)
        if (existingIndex >= 0) {
          plans[existingIndex] = currentPlan
        } else {
          plans.push(currentPlan)
        }
        
        set({ plans: [...plans] })
      },
    }),
    {
      name: 'financial-plan-storage',
    }
  )
)
